/**
 * Helper: Utils
 * ------------------------------------------------------------------------------
 * Useful utility functions.
 *
 * @namespace utils
 */
import Cookies from 'js-cookie'

/**
 * Get the current store environment.
 * Returns true when the dev or staging environment is identified.
 * Note: the `-dev` environment is never really used, but included for coverage.
 * @returns {Boolean}
 */
export function getCurrentStore() {
  const storeName = mint.shop.permanentDomain.split('.myshopify.com')[0]
  const stagingEnvironments = ['-staging', '-dev']

  const isStaging = stagingEnvironments.find((name) => {
    return storeName.includes(name)
  })

  return Boolean(isStaging)
}

/**
 * Debounce function.
 * - Rate-limiting for functions.
 * - Runs a specific callback after a given delay
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - Amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Generates a cart localised permalink URL based on customer information.
 *
 * @param {Object} customerInfo - Customer details (email, first name, last name).
 * @returns {string} - The complete cart permalink URL.
 */
export function generateCheckoutPermalink(customerInfo) {
  const baseUrl = new URL('https:' + mint.shop.localisedShopUrl + 'checkout')

  if (!customerInfo) {
    return baseUrl.href
  }

  // Set the query parameters based on customer email/firstname/lastname
  baseUrl.searchParams.set('checkout[email]', customerInfo.email)
  baseUrl.searchParams.set('checkout[shipping_address][first_name]', customerInfo.firstName)
  baseUrl.searchParams.set('checkout[shipping_address][last_name]', customerInfo.lastName)

  return baseUrl.href
}

/**
 * Returns a function, that, as long as it continues to be invoked,
 * will not be triggered. The function will be called after it stops
 * being called for N milliseconds. If `immediate` is passed, trigger
 * the function on the leading edge, instead of the trailing.
 *
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout

  return function(...args) {
    if (immediate && !timeout) {
      callback.apply(this, args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(() => {
      callback.apply(this, args)
    }, wait)
  }
}

/**
 * Get unique objects from object literal.
 * @param {Object} object - Array/Object to remove duplicates from.
 */
export function unique(object) {
  return object.filter((value, index, self) => self.indexOf(value) === index)
}

/**
 * Get the difference of two arrays.
 * @param {Array} array - An array containing two arrays to diff.
 */
export function difference(array1, array2) {
  return array1.concat(array2).filter((value, index, self) => {
    return self.indexOf(value) === self.lastIndexOf(value)
  })
}

/**
 * Compare two arrays of shallow objects and see if they are the same.
 * @example:
 * const array1 = [
 *  {
 *    hello: 'world'
 * },
 * {
 *   foo: 'bar'
 * }
 *
 *
 * const array2 = [
 *  {
 *    hello: 'world'
 * },
 * {
 *   foo: 'notmatch'
 * }
 *
 * const isMatch = arrayObjectsEqual(array1, array2)
 *
 * @param {Array} array1
 * @param {Array} array2
 * @returns {Boolean}
 */
export function arrayObjectsEqual(array1, array2){
  if (array1.length !== array2.length) {
    return false
  }

  return array1.every(
    every1 => array2.some(
      every2 => Object.keys(every1).every(key => every1[key] === every2[key]),
    ),
  )
}

/**
 * Combine two objects using properties as the override.
 * @param {object} defaults - Defaults options defined in script.
 * @param {object} properties - Options defined by user.
 * @return {object} - Defaults modified options.
 */
export function extendDefaults(defaults, properties) {
  for (const property in properties) {
    if (properties != null && typeof properties !== 'undefined') {
      defaults[property] = properties[property]
    }
  }

  return defaults
}

/**
  * Retrieves input data from a form.
  * @param {HTMLForm} form - HTML form elements.
  * @returns {Object} - Form data as an object literal.
  */
export function formToJSON(elements) {
  return [].reduce.call(elements, (data, element) => {
    if (element.name && element.value) {
      data[element.name] = element.value
    }
    return data
  }, {})
}

/**
 * Serialize all form data into a query string.
 * @param {Node} form - The form to serialize.
 * @return {String} - The serialized form data.
 */
export function serialize(form) {
  const serialized = []

  /**
   * Loop through each field in the form.
   */
  for (let index = 0; index < form.elements.length; index++) {
    const field = form.elements[index]

    // Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
    if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'submit' || field.type === 'button') { continue }

    /**
     * If a multi-select, get all selections.
     */
    if (field.type === 'select-multiple') {
      for (let counter = 0; counter < field.options.length; counter++) {
        if (!field.options[counter].selected) {
          continue
        }

        serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.options[counter].value)}`)
      }
    } else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
      serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`)
    }
  }

  return serialized.join('&')
}

/**
 * Shortcut function to add an event listener.
 * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {String} event -The event type.
 * @param {Node} elem - The element to attach the event to (optional, defaults to window).
 * @param {Function} callback - The callback to run on the event.
 * @param {Boolean} capture - If true, forces bubbling on non-bubbling events.
 */
export function on(event, elem = window, callback, capture = false) {

  /**
   * If only a string is passed into the element parameter.
   */
  if (typeof elem === 'string') {
    document.querySelector(elem).addEventListener(event, callback, capture)
    return
  }

  /**
   * If an element is not defined in parameters, then shift callback across.
   */
  if (typeof elem === 'function') {
    window.addEventListener(event, elem)
    return
  }

  /**
   * Default listener.
   */
  elem.addEventListener(event, callback, capture)
}

/**
 * Get all siblings of an element.
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {Node} elem - The element you want get the siblings of.
 * @return {Array} - The siblings.
 */
export function getSiblings(elem) {
  return Array.prototype.filter.call(elem.parentNode.children, (sibling) => {
    return sibling !== elem
  })
}

/**
 * Render function.
 * @param {String} template - The template string to render.
 * @param {Node} node - The node to render into.
 */
export function render(template, node) {
  if (!node) {
    return
  }

  node.innerHTML = template
}

/**
 * Browser detect and return a string
 * - https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
 * @returns {String} browser string.
 */
export function getBrowser() {
  const userAgent = window.navigator.userAgent

  // The order matters here, and this may report false positives for unlisted browsers.
  if (userAgent.indexOf('Firefox') > -1) {
    // 'Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0'
    return 'Firefox'
  } else if (userAgent.indexOf('Opera') > -1) {
    return 'Opera'
  } else if (userAgent.indexOf('Trident') > -1) {
    // 'Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko'
    return 'Internet Explorer'
  } else if (userAgent.indexOf('Edge') > -1) {
    // 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299'
    return 'Edge'
  } else if (userAgent.indexOf('Chrome') > -1) {
    // 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36'
    return 'Chrome'
  } else if (userAgent.indexOf('Safari') > -1) {
    // 'Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306'
    return 'Safari'
  }

  return 'unknown'
}

/* Get outerHTML of elements, taking care
 * of SVG elements in IE as well.
 *
 * @param {Element} el
 * @return {String}
 */
export function getOuterHTML(el) {
  if (el.outerHTML) {
    return el.outerHTML
  } else {
    const container = document.createElement('div')
    container.appendChild(el.cloneNode(true))
    return container.innerHTML
  }
}

/**
 * Check if object is a HTMLElement.
 * @param {object} element - HTML element to check.
 */
export function isElement(element) {
  return element instanceof window.Element || element instanceof window.HTMLDocument
}

/**
 * Get query parameters as an object.
 * @return {Object} - Query object.
 */
export function getQueryParamsObject() {
  const result = {}
  const parser = document.createElement('a')

  parser.href = window.location.href

  const query = parser.search.substring(1)
  const vars = query.split('&')

  for (let index = 0; index < vars.length; index++) {
    const pair = vars[index].split('=')
    result[pair[0]] = decodeURIComponent(pair[1])
  }

  return result
}

export function rafAsync() {
  return new Promise(resolve => {
    requestAnimationFrame(resolve) //faster than set time out
  })
}

export function checkElementBackgroundImage(selector) {
  if (document.querySelector(selector) === null) {
    return Promise.reject(false)
  }
  if (document.querySelector(selector).style.backgroundImage.length < 1) {
    return rafAsync().then(() => checkElementBackgroundImage(selector))
  } else {
    return Promise.resolve(true)
  }
}

export function slideDown(elem) {
  elem.style.maxHeight = '1000px'
  // We're using a timer to set opacity = 0 because setting max-height = 0 doesn't (completely) hide the element.
  elem.style.opacity = '1'
}

/**
 * Slide element up (like jQuery's slideUp)
 * @param  {Node} elem Element
 * @return {[type]}      [description]
 */
export function slideUp(elem) {
  elem.style.maxHeight = '0'
  once(1, () => {
    elem.style.opacity = '0'
  })
}

/**
 * Get a specified cookie's value using `js-cookie`.
 * @param {String} name ID of the cookie
 * @returns {String|Boolean}
 */
export function getCookieValue(name) {
  const cookieValue = Cookies.get(name)
  return cookieValue ? decodeURIComponent(cookieValue) : false
}

/**
 * Call once after timeout
 * @param  {Number}   seconds  Number of seconds to wait
 * @param  {Function} callback Callback function
 */
function once(seconds, callback) {
  let counter = 0
  const time = window.setInterval(() => {
    counter++
    if (counter >= seconds) {
      callback()
      window.clearInterval(time)
    }
  }, 400)
}

/**
 * Get the user's country and currency and format for querying Attraqt.
 * - Return the base country and currency if the user-specific values do not
 *   exist.
 * @returns {Object}
 */
export function getAttraqtLocaleAttributes() {
  const country = mint?.shop?.country ?? mint?.shop?.baseCountry
  const currency = mint?.shop?.currency ?? mint?.shop?.baseCurrency

  /**
   * In case of missing attributes, return an empty object.
   * - Returns an empty object instead of `false` to allow for this function's
   *   output to be used with a spread operator (...).
   */
  if (!country || !currency) {
    return {}
  }

  return {
    'mv_country': country.toLowerCase(),
    'mv_currency': currency.toLowerCase(),
  }
}

/**
 * Parse a stringified JSON object.
 * - Repeats if the original object was stringified more than once.
 * @param {String} jsonString - Stringified JSON object.
 * @returns {Boolean|Object}
 */
export function parseJSONUntilObject(jsonString) {
  let parsedData = JSON.parse(jsonString)

  while (typeof parsedData === 'string') {
    parsedData = JSON.parse(parsedData)
  }

  return parsedData
}

/**
 * Set a value for an index in an array, without modifying the original array
 * @param {Array} arr - Existing array
 * @param {Number} index - Index to set to value
 * @param {Any} newValue - Value to set at index
 * @returns {Array}
 */
export function setByIndex(arr, index, newValue) {
  const left = index > 0 ? arr.slice(0, index) : []
  const right = index < arr.length - 1 ? arr.slice(index + 1) : []
  const newArr = [
    ...left, newValue, ...right,
  ]
  return newArr

}

/**
 * Remove an index in an array, without modifying the original array
 * @param {Array} arr - Existing array
 * @param {Number} index - Index to remove
 * @returns {Array}
 */
export function removeByIndex(arr, index) {
  const left = index > 0 ? arr.slice(0, index) : []
  const right = index < arr.length - 1 ? arr.slice(index + 1) : []

  return [...left, ...right]

}

/**
 * Takes a regex, returns ia function that returns the match without context or
 * an empty string if no match was found.
 * @param {Regex} regex - Regex to match against
 * @returns {String}
 */
export function safeMatch(regex) {
  return function(str) {
    const res = str.match(regex)
    if (res) {
      return res[0]
    }
    return ''
  }
}

/**
 * Attempt to find a pack shot image in a given array.
 * @param {Array} images
 * @returns {Object}
 */
export function getPackShotImage(images = []) {
  if (!images.length) {
    return false
  }

  const regex = window.mint.flags.useCloudinaryCDN ? /_7(?:_|$)/ : /[-a-z]\d?_7[_-]/
  const imageIndex = images.findIndex(image => image.src && regex.test(image.src))

  if (imageIndex !== -1) {
    return {
      image: images[imageIndex],
      index: imageIndex,
    }
  }

  /**
   * Fall back to the second-last image, or the first image
   */
  const imageCount = images.length
  const defaultIndex = imageCount >= 2 ? imageCount - 2 : 0

  return {
    image: images[defaultIndex],
    index: defaultIndex,
  }
}

/**
 * Returns all focusable elements within a given container.
 * - Focusable elements include links, buttons, inputs, and other interactive
 * elements.
 *
 * @param {HTMLElement} container - The container element to search within.
 * @returns {HTMLElement[]} - An array of focusable elements within the container.
 */
export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^=\'-\']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe',
    ),
  )
}
